/**
 * Keeps the entitlements store (store/entitlementsStore.js) in step with the
 * account: the plan name at once, then the server's limits from
 * `GET /auth/usage` (which carries the local_* keys since Plans v2).
 *
 * Called by the auth store on sign-in, session restore and every plan change;
 * `resetEntitlements` on sign-out. Never throws: a failed usage call leaves the
 * plan-name defaults (paid = unlimited locally, otherwise Free's caps).
 *
 * Signed out nothing is served at all (owner, 16.10.2026): the app shows only
 * the sign-in page and the MCP server does not run, so signed-out caps are
 * Free's and nothing reads them. A plain sign-out keeps the stored plan
 * claim (plans/planClaim.js); "Remove from device" forgets it.
 *
 * Every refresh is a generation: a newer refresh, a sign-out
 * (resetEntitlements) or a different signed-in account makes an older one's
 * late answers (claim, usage) drop instead of landing on the wrong session.
 */
import { useEntitlementsStore } from '../store/entitlementsStore';
import { useAuthStore } from '../store/authStore';
import { addSessionEndedListener } from '../services/channelService';
import { loadCachedPlanClaim, refreshPlanClaim } from './planClaim';
import { capabilitiesFromUsage } from './cloudState';

let generation = 0;

/** The signed-in account's id, or null (signed out, or the store not ready). */
function signedInUserId() {
  try {
    return useAuthStore?.getState?.().user?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * @param {{ id?: string|number, plan?: string } | null} user
 * @param {() => Promise<{ limits?: object, capabilities?: string[] }>} getUsage
 * @param {() => Promise<{ token: string }>} [getPlanClaim]
 */
export async function refreshEntitlements(user, getUsage, getPlanClaim) {
  const store = useEntitlementsStore.getState();
  generation += 1;
  if (!user) {
    store.reset();
    return false;
  }
  const mine = generation;
  const isCurrent = () => {
    if (mine !== generation) return false;
    const current = signedInUserId();
    return current == null || user.id == null || String(current) === String(user.id);
  };
  store.setPlan(user.plan);
  // Card 2.4: the signed claim first, cached (offline) then fresh. A changed
  // plan name makes a cached claim stale, so it only counts when it agrees.
  await loadCachedPlanClaim(user.id, isCurrent);
  if (!isCurrent()) return false;
  if (useEntitlementsStore.getState().plan !== String(user.plan || 'free').toLowerCase()) store.setPlan(user.plan);
  if (await refreshPlanClaim(user.id, getPlanClaim, isCurrent)) return true;
  if (typeof getUsage !== 'function') return false;
  try {
    const usage = await getUsage();
    if (!usage?.limits || !isCurrent()) return false;
    // A claim (card 2.4) outranks usage; never overwrite it with a weaker source.
    if (useEntitlementsStore.getState().source === 'claim') return false;
    // /auth/usage carries no capability list today: derived the server's way
    // (plans/cloudState.js), so `cloud_sync` is known without a claim too.
    store.setSnapshot({ plan: user.plan, limits: usage.limits, capabilities: capabilitiesFromUsage(usage) ?? undefined, source: 'usage' });
    return true;
  } catch {
    return false;
  }
}

export const resetEntitlements = () => {
  generation += 1;
  useEntitlementsStore.getState().reset();
};

// Signed out (any path: logout, revoked session, session cap) = Free's caps.
// The stored claim stays (a plain sign-out); "Remove from device" forgets it.
addSessionEndedListener(resetEntitlements);
